import { OFFLINE_DB_NAME, OFFLINE_DB_VERSION } from "./schema";

let dbPromise = null;

function openDatabase() {
  if (typeof window === "undefined" || !window.indexedDB) {
    return Promise.reject(new Error("IndexedDB no disponible"));
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains("cached_records")) {
        const store = db.createObjectStore("cached_records", {
          keyPath: ["table_name", "record_id"],
        });
        store.createIndex("by_table", "table_name", { unique: false });
        store.createIndex("by_user", "user_id", { unique: false });
        store.createIndex("by_updated_at", "updated_at", { unique: false });
        store.createIndex("by_cached_at", "cached_at", { unique: false });
      }

      if (!db.objectStoreNames.contains("pending_operations")) {
        const store = db.createObjectStore("pending_operations", {
          keyPath: "id",
        });
        store.createIndex("by_status", "status", { unique: false });
        store.createIndex("by_table", "table_name", { unique: false });
        store.createIndex("by_record", "record_id", { unique: false });
        store.createIndex("by_created_at", "created_at", { unique: false });
      }

      if (!db.objectStoreNames.contains("sync_metadata")) {
        db.createObjectStore("sync_metadata", { keyPath: "table_name" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("No se pudo abrir IndexedDB"));
  });
}

export async function getOfflineDb() {
  if (!dbPromise) {
    dbPromise = openDatabase();
  }
  return dbPromise;
}

function runTransaction(storeName, mode, handler) {
  return getOfflineDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        let result;

        try {
          result = handler(store, tx);
        } catch (err) {
          reject(err);
          return;
        }

        tx.oncomplete = () => resolve(result && "result" in result ? result.result : result);
        tx.onerror = () => reject(tx.error || new Error("IndexedDB transaction error"));
        tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction aborted"));
      })
  );
}

export async function putMany(storeName, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  return runTransaction(storeName, "readwrite", (store) => {
    for (const row of rows) {
      store.put(row);
    }
  });
}

export async function putOne(storeName, row) {
  if (!row) return;
  return runTransaction(storeName, "readwrite", (store) => {
    store.put(row);
  });
}

export async function getOne(storeName, key) {
  return runTransaction(storeName, "readonly", (store) => store.get(key));
}

export async function getAllByIndex(storeName, indexName, value) {
  return runTransaction(storeName, "readonly", (store) => {
    const index = store.index(indexName);
    return index.getAll(value);
  });
}

export async function getAll(storeName) {
  return runTransaction(storeName, "readonly", (store) => store.getAll());
}

export async function clearStore(storeName) {
  return runTransaction(storeName, "readwrite", (store) => {
    store.clear();
  });
}
